import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import Banner from '../../content-types/banner/Banner';
import Card from '../../content-types/card/Card';
import Drawer from '../../content-types/drawer/Drawer';
import LinkItemCollection from '../../content-types/link-group/LinkItemCollection';
import Modal from '../../content-types/modal/Modal';
import SmallCardCollection from '../../content-types/small-card-collection/SmallCardCollection';
import type { Action, Content, LoadView } from '../../global-types/Content';
import { trackAI, trackException } from '../../helpers/telemetryHelper';
import useShadowCSS from '../../hooks/useShadowCSS';

export interface InxsWebProps {
  instanceId: string;
  shadowRoot: ShadowRoot;
}

export type ActionEventDetail = {
  loadViews?: LoadView[];
  nextContent?: Content | null;
};

const DATA_ELEMENT_ID = 'inxs-web:received-data';
const DATA_ACTION_EVENT = 'inxs-web:action';

function readContentFromDom(id: string): Content | null {
  const el = document.getElementById(DATA_ELEMENT_ID);
  if (!el?.textContent?.trim()) return null;
  const parsed = JSON.parse(el.textContent);
  const arr: Content[] = Array.isArray(parsed) ? parsed : [parsed];
  return arr.find((c) => c.instanceId === id) ?? null;
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 0 }, mutations: { retry: 0 } },
});

const InxsWeb = ({ instanceId, shadowRoot }: InxsWebProps) => {
  useShadowCSS(shadowRoot);
  const [content, setContent] = useState<Content | null>(null);
  const [viewId, setViewId] = useState<number | undefined>(undefined);

  useEffect(() => {
    try {
      const found = readContentFromDom(instanceId);
      if (found) {
        setContent(found);
      } else {
        trackAI(`No content found for instanceId: ${instanceId}`, SeverityLevel.Warning);
      }
    } catch (err) {
      trackException(err as Error, { instanceId: instanceId });
    }
  }, [instanceId]);

  useEffect(() => {
    setViewId(content?.initialView);
  }, [content]);

  useEffect(() => {
    const onAction = (event: Event) => {
      const detail = (event as CustomEvent<ActionEventDetail>).detail;

      if (detail?.nextContent?.instanceId === instanceId) {
        setContent(detail.nextContent);
        return;
      }

      if (!content) return;

      const loadViews = detail?.loadViews;
      if (!Array.isArray(loadViews) || loadViews.length === 0) return;

      const match = loadViews.find((lv) => lv.contentCode === content.contentCode);
      if (!match) return;

      setViewId(match.viewId ?? undefined);
    };

    window.addEventListener(DATA_ACTION_EVENT, onAction as EventListener);
    return () => window.removeEventListener(DATA_ACTION_EVENT, onAction as EventListener);
  }, [content, content?.contentCode]);

  const dispatchActionEvent = (action: Action, nextContent?: Content | null) => {
    window.dispatchEvent(
      new CustomEvent<ActionEventDetail>(DATA_ACTION_EVENT, {
        detail: { loadViews: action.loadViews, nextContent: nextContent ?? null },
      }),
    );
  };

  if (!content) return null;

  const renderCollection = () => {
    const firstChildType = content.content?.[0]?.contentType;
    switch (firstChildType) {
      case 'smallCard':
        return <SmallCardCollection content={content} viewId={viewId!} dispatchActionEvent={dispatchActionEvent} />;
      case 'linkItem':
        return <LinkItemCollection content={content} viewId={viewId!} dispatchActionEvent={dispatchActionEvent} />;
      default:
        return null;
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      {viewId !== undefined && (
        <>
          {content.contentType === 'banner' && (
            <Banner content={content} viewId={viewId} dispatchActionEvent={dispatchActionEvent} />
          )}
          {content.contentType === 'modal' && (
            <Modal content={content} viewId={viewId} dispatchActionEvent={dispatchActionEvent} />
          )}
          {content.contentType === 'drawer' && (
            <Drawer content={content} viewId={viewId} dispatchActionEvent={dispatchActionEvent} />
          )}
          {content.contentType === 'card' && (
            <Card content={content} viewId={viewId} dispatchActionEvent={dispatchActionEvent} />
          )}
          {content.contentType === 'collection' && renderCollection()}
        </>
      )}
    </QueryClientProvider>
  );
};

export default InxsWeb;
